import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
} from '@material-ui/core'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import ResponsiveDialog from 'app/components/dialog/ResponsiveDialog'
import { useAppName, useAppLogo } from 'commons/usePartner'
import { all } from 'bluebird'
import * as R from 'ramda'

const fragmentText = (ctx, text, maxWidth) => {
  const words = text.split(' ')
  const lines = []
  let line = ''
  if (ctx.measureText(text).width < maxWidth) {
    return [text]
  }
  while (words.length > 0) {
    let split = false
    while (ctx.measureText(words[0]).width >= maxWidth) {
      const tmp = words[0]
      words[0] = tmp.slice(0, -1)
      if (!split) {
        split = true
        words.splice(1, 0, tmp.slice(-1))
      } else {
        words[1] = tmp.slice(-1) + words[1]
      }
    }
    if (ctx.measureText(line + words[0]).width < maxWidth) {
      line += words.shift() + ' '
    } else {
      lines.push(line)
      line = ''
    }
    if (words.length === 0) {
      lines.push(line)
    }
  }
  return lines
}

function createBanner(
  canvas,
  font,
  placeName,
  backgroundImageURL,
  qrCodeImageURL,
  placeLogoImageURL,
  appLogoImageURL,
) {
  return all(
    [backgroundImageURL, qrCodeImageURL, placeLogoImageURL, appLogoImageURL]
      .filter(R.complement(R.isNil))
      .map(loadImage),
  ).then(([backgroundImage, qrCodeImage, placeLogoImage, appLogoImage]) => {
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = 'rgb(255, 255, 255)'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(
      backgroundImage,
      0,
      0.065 * canvas.height,
      canvas.width,
      0.9375 * canvas.height,
    )

    ctx.font = font
    ctx.fillStyle = 'rgb(64, 64, 64)'
    ctx.textAlign = 'center'
    const lines = fragmentText(ctx, placeName, 0.87 * canvas.width)
    lines.forEach((line, i) =>
      ctx.fillText(
        line,
        0.5 * canvas.width,
        i * 0.025 * canvas.height + 0.0375 * canvas.height,
      ),
    )
    ctx.fillStyle = '#00aeef'

    ctx.drawImage(
      qrCodeImage,
      0.2276 * canvas.width,
      0.3516 * canvas.height,
      0.5494 * canvas.width,
      0.3641 * canvas.height,
    )
    if (placeLogoImage) {
      const ratio = placeLogoImage.naturalWidth / placeLogoImage.naturalHeight
      const heightLogo = 0.2391 * canvas.height

      ctx.drawImage(
        placeLogoImage,
        (canvas.width - heightLogo * ratio) / 2,
        0.088 * canvas.height,
        heightLogo * ratio,
        heightLogo,
      )
    }

    if (appLogoImage) {
      const ratio = appLogoImage.naturalWidth / appLogoImage.naturalHeight
      const maxWidth = canvas.width * 0.7
      const maxHeight = canvas.height * 0.2 * 0.6
      const maxRatio = maxWidth / maxHeight
      const isImageRatioWiderThanAvailableSpace = ratio > maxRatio
      // Use maxWidth when the appLogo has wider ratio compared to available space (maxWidth),
      // then the height calculated from `maxWidth / ratio` will never longer than maxHeight.
      // on the otherhand, use maxHeight when the appLogo has taller ratio.
      const width = isImageRatioWiderThanAvailableSpace
        ? maxWidth
        : maxHeight * ratio
      const height = isImageRatioWiderThanAvailableSpace
        ? maxWidth / ratio
        : maxHeight

      ctx.drawImage(
        appLogoImage,
        (canvas.width - width) / 2,
        0.9125 * canvas.height - height / 2,
        width,
        height,
      )
    }

    ctx.save()
    return canvas
  })
}

function loadImage(src) {
  return new Promise((resolve) => {
    const image = new Image()
    image.src = src
    image.crossOrigin = 'anonymous'
    image.onload = function () {
      resolve(image)
    }
  })
}

function canvasToBlob(fileType) {
  return (canvas) =>
    new Promise((resolve) => {
      canvas.toBlob(function (blob) {
        resolve(blob)
      }, fileType)
    })
}

function startDownloading(url, downloadFilename) {
  const link = document.createElement('a')
  link.href = url
  link.download = downloadFilename
  link.click()
}

const PrintableStatus = {
  loading: 'loading',
  completed: 'completed',
  failed: 'failed',
}

export default ({
  open,
  onClose,
  classes,
  placeName,
  backgroundImageURL,
  placeLogoImageURL,
  qrCodeImageURL,
}) => {
  const previewCanvasRef = useRef(null)
  const printableCanvasRef = useRef(null)
  const appName = useAppName()
  const { bannerRev: appLogoImageURL } = useAppLogo()
  const [isPreviewLoading, setPreviewLoading] = useState(false)
  const [printableStatus, setPrintableStatus] = useState({ status: undefined })

  const isPrintableLoading = printableStatus.status === PrintableStatus.loading

  useEffect(() => {
    if (!previewCanvasRef.current) {
      return
    }
    setPreviewLoading(true)
    createBanner(
      previewCanvasRef.current,
      'bold 16px Arial',
      placeName,
      backgroundImageURL,
      qrCodeImageURL,
      placeLogoImageURL,
      appLogoImageURL,
    ).then(() => {
      setPreviewLoading(false)
    })
  }, [
    previewCanvasRef,
    setPreviewLoading,
    placeName,
    backgroundImageURL,
    qrCodeImageURL,
    placeLogoImageURL,
    appLogoImageURL,
  ])

  function downloadPrintableBanner() {
    function download(printableURL) {
      startDownloading(printableURL, `${appName}_QR_Code.png`)
    }

    if (printableStatus.status === PrintableStatus.completed) {
      download(printableStatus.printableURL)
      return
    }
    if (
      !printableCanvasRef.current ||
      R.includes(printableStatus.status, [
        PrintableStatus.loading,
        PrintableStatus.completed,
      ])
    ) {
      return
    }

    setPrintableStatus({ status: PrintableStatus.loading })
    createBanner(
      printableCanvasRef.current,
      'bold 74px Arial',
      placeName,
      backgroundImageURL,
      qrCodeImageURL,
      placeLogoImageURL,
      appLogoImageURL,
    )
      .then(canvasToBlob('image/png'))
      .then((blob) => {
        const printableURL = URL.createObjectURL(blob)
        setPrintableStatus({ status: PrintableStatus.completed, printableURL })
        download(printableURL)
      })
  }

  return (
    <div>
      <ResponsiveDialog
        isFullScreen={false}
        open={open}
        onClose={onClose}
        disableBackdropClick={true}
      >
        <DialogTitle className="pb-4 mt-8">
          <Grid container spacing={2}>
            <Grid item xs={11}>
              <FormattedMessage id="qr.button" />
            </Grid>
            <Grid item xs={1} className={classes.classClose} onClick={onClose}>
              <Icon className={classes.iconSize} color="action">
                close
              </Icon>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent className="pt-4">
          <div className="relative flex flex-col">
            <p>
              <FormattedMessage id="qr.text" />
            </p>
            <canvas
              className=""
              ref={previewCanvasRef}
              width={528}
              height={797}
            />
            <canvas
              className="hidden"
              ref={printableCanvasRef}
              width={2439}
              height={3680}
            />
            {isPreviewLoading && (
              <div className="absolute inset-0 flex w-full h-full items-center justify-center">
                <CircularProgress size={48} color="secondary" />
              </div>
            )}
          </div>
        </DialogContent>
        <div className="flex justify-end">
          <DialogActions className="justify-between p-16 mb-8">
            <PrimaryButton
              isLoading={isPrintableLoading}
              labelId="qr.download"
              disabled={isPrintableLoading}
              type="submit"
              onClick={() => downloadPrintableBanner()}
            />
          </DialogActions>
        </div>
      </ResponsiveDialog>
    </div>
  )
}
